// utils.ts - Utility functions for the Dance Video Segmenter

import {
  ComboEdge,
  ComboRange,
  Segment,
  CheckpointData,
  VideoSettings,
  DemoData,
  SessionData,
  normalizeVideoSettingsViewCrop
} from './types';
import { saveSession as saveToFirebase, loadSession as loadFromFirebase } from '../firebase/sessionService';
import { doc, collection } from 'firebase/firestore';
import { ref } from 'firebase/storage';
import { db, storage } from '../firebase/config'
import {
  SOURCE_SEGMENT_COLOR,
  COMBO_SEGMENT_COLOR,
  USERS_COLL,
  USER_VIDEOS_SUBCOLL,
  USER_PROJECTS_SUBCOLL,
  USER_PLAYLISTS_SUBCOLL,
  STORAGE_USER_ROOT,
  STORAGE_USER_VIDEOS,
} from '../constants';
import { DEFAULT_VIDEO_EXTENSION } from '../utils/videoFileTypes';

/**
 * Load demo data from JSON file
 */
export const loadDemoData = async (): Promise<DemoData> => {
  try {
    const response = await fetch('/demo/demo-segments.json');
    if (!response.ok) {
      throw new Error(`Failed to load demo data: ${response.status}`);
    }
    const demoData: DemoData = await response.json();
    return {
      ...demoData,
      segments: (demoData.segments ?? []).map(segment => ({
        ...segment,
        color: segment.isCombo ? COMBO_SEGMENT_COLOR : SOURCE_SEGMENT_COLOR
      }))
    };
  } catch (error) {
    console.error('Error loading demo data:', error);
    // Fallback demo data if file doesn't exist
    return {
      videoFile: 'demo-dance.mp4',
      title: 'Demo Dance Tutorial',
      description: 'A sample dance tutorial showing how to segment choreography',
      segments: [
        {
          id: 'demo-1',
          name: 'Intro & Setup',
          startTime: 0,
          endTime: 15,
          color: SOURCE_SEGMENT_COLOR,
          transitionDuration: 0.5,
          isLearned: false,
          isDisabled: false
        },
        {
          id: 'demo-2',
          name: 'Basic Step',
          startTime: 14.7,
          endTime: 28,
          color: SOURCE_SEGMENT_COLOR,
          transitionDuration: 0.5,
          isLearned: false,
          isDisabled: false
        },
        {
          id: 'demo-3',
          name: 'Turn Sequence',
          startTime: 27.7,
          endTime: 42,
          color: SOURCE_SEGMENT_COLOR,
          transitionDuration: 0.5,
          isLearned: false,
          isDisabled: false
        },
        {
          id: 'demo-4',
          name: 'Combination',
          startTime: 41.7,
          endTime: 60,
          color: SOURCE_SEGMENT_COLOR,
          transitionDuration: 0.5,
          isLearned: false,
          isDisabled: false
        }
      ],
      settings: {
        defaultSpeed: 1.0,
        defaultMirror: false,
        autoLoop: false,
        transitionType: 'none',
        defaultMuted: false
      }
    };
  }
};

/**
 * Get demo video URL
 */
export const getDemoVideoUrl = (videoFile: string = 'demo-dance.mp4'): string => {
  return `/videos/${videoFile}`;
};


/**
 * Format time (seconds) to MM:SS format

export const formatTime = (timeInSeconds: number): string => {
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = Math.floor(timeInSeconds % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};
 */

/**
 * Return the stable source segment color.
 */
export const getRandomColor = (
  usedColors: string[] = [],
  previousColor?: string
): string => {
  void usedColors;
  void previousColor;
  return SOURCE_SEGMENT_COLOR;
};

/**
 * Save checkpoints to a JSON file
 */
export const saveCheckpoints = (
  segments: Segment[], 
  videoFileName: string, 
  settings: VideoSettings
): void => {
  if (segments.length === 0 || !videoFileName) return;
  
  const data: CheckpointData = {
    filename: videoFileName,
    segments: normalizeCombos(segments),
    settings: normalizeVideoSettingsViewCrop(settings) as VideoSettings
  };
  
  const jsonData = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonData], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  
  const a = document.createElement('a');
  a.href = url;
  a.download = `${videoFileName.split('.')[0]}-checkpoints.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Load checkpoints from a file
 */
export const loadCheckpoints = (
  file: File,
  onSuccess: (data: CheckpointData) => void,
  onError: (error: string) => void
): void => {
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const data = JSON.parse(e.target?.result as string) as CheckpointData;
      onSuccess(data);
    } catch (error) {
      console.error('Error loading checkpoints:', error);
      onError('Invalid checkpoint file');
    }
  };
  reader.readAsText(file);
};

/**
 * Create a new segment
 */
export const createSegment = (
  segmentNumber: number,
  startTime: number,
  endTime: number,
  usedColors: string[],
  transitionDuration: number
): Segment => {
  return {
    id: `segment-${Date.now()}`,
    name: `Segment ${segmentNumber}`,
    startTime,
    endTime,
    color: getRandomColor(usedColors, usedColors[usedColors.length - 1]),
    transitionDuration,
    isLearned: false,
    isDisabled: false,
    segmentNameEdited: false
  };
};

/**
 * Whether a segment is a combo (a derived segment built from other segments).
 *
 * Prefers the explicit `isCombo` flag / `sourceSegmentIds` on the combo model.
 * Falls back to the legacy `_`-in-name convention so existing saved data keeps
 * working until it is migrated. This is the single source of truth for "is this
 * a combo?" — do not sniff `name.includes('_')` directly elsewhere.
 */
export const isCombo = (segment: Segment): boolean => {
  if (segment.isCombo || (segment.sourceSegmentIds && segment.sourceSegmentIds.length > 0)) {
    return true;
  }
  return segment.name.includes('_');
};

export const MIN_COMBO_SOURCE_SEGMENTS = 2;

export const getFirstComboId = (segments: Segment[]): string | null => {
  return segments.find(segment =>
    isCombo(segment) &&
    (segment.sourceSegmentIds?.length ?? MIN_COMBO_SOURCE_SEGMENTS) >= MIN_COMBO_SOURCE_SEGMENTS
  )?.id ?? null;
};

export const getSourceSegments = (segments: Segment[]): Segment[] => {
  return [...segments]
    .filter(segment => !isCombo(segment))
    .sort((a, b) => a.startTime - b.startTime);
};

const GENERATED_SEGMENT_NAME_PATTERN = /^Segment\s+\d+$/i;

export const isGeneratedSegmentName = (name: string): boolean => {
  return GENERATED_SEGMENT_NAME_PATTERN.test(name.trim());
};

export const renameGeneratedSourceSegmentsByTimeline = (segments: Segment[]): Segment[] => {
  const timelinePositionById = new Map(
    getSourceSegments(segments).map((segment, index) => [segment.id, index + 1] as const)
  );
  let didChange = false;

  const renamedSegments = segments.map(segment => {
    if (isCombo(segment) || segment.segmentNameEdited || !isGeneratedSegmentName(segment.name)) return segment;

    const timelinePosition = timelinePositionById.get(segment.id);
    if (!timelinePosition) return segment;

    const timelineName = `Segment ${timelinePosition}`;
    if (segment.name === timelineName) return segment;

    didChange = true;
    return {
      ...segment,
      name: timelineName
    };
  });

  return didChange ? renamedSegments : segments;
};

export interface BoundaryDraftSegmentResult {
  segments: Segment[];
  addedSegment: Segment | null;
}

const MIN_BOUNDARY_DRAFT_SEGMENT_DURATION = 0.1;

const roundBoundaryDraftTime = (value: number): number => {
  return Math.round(value * 100) / 100;
};

const hasMinimumBoundaryDuration = (
  startTime: number,
  endTime: number,
  minimumDuration = MIN_BOUNDARY_DRAFT_SEGMENT_DURATION
): boolean => {
  return endTime - startTime >= minimumDuration;
};

export const addBoundaryDraftSegmentAtTime = (
  draftSegments: Segment[],
  playheadTime: number,
  transitionDuration: number,
  minimumDuration = MIN_BOUNDARY_DRAFT_SEGMENT_DURATION
): BoundaryDraftSegmentResult => {
  const sourceSegments = getSourceSegments(draftSegments);
  const safePlayheadTime = Number.isFinite(playheadTime)
    ? roundBoundaryDraftTime(Math.max(0, playheadTime))
    : 0;
  const usedColors = sourceSegments.map(segment => segment.color);
  const createDraftSegment = (startTime: number, endTime: number): Segment => (
    createSegment(
      sourceSegments.length + 1,
      roundBoundaryDraftTime(startTime),
      roundBoundaryDraftTime(endTime),
      usedColors,
      transitionDuration
    )
  );
  const buildDraftResult = (
    nextSegments: Segment[],
    addedSegment: Segment
  ): BoundaryDraftSegmentResult => {
    const numberedSegments = renameGeneratedSourceSegmentsByTimeline(getSourceSegments(nextSegments));
    return {
      segments: numberedSegments,
      addedSegment: numberedSegments.find(segment => segment.id === addedSegment.id) ?? addedSegment
    };
  };

  if (sourceSegments.length === 0) {
    if (!hasMinimumBoundaryDuration(0, safePlayheadTime, minimumDuration)) {
      return { segments: sourceSegments, addedSegment: null };
    }

    const firstSegment = createDraftSegment(0, safePlayheadTime);
    return buildDraftResult([firstSegment], firstSegment);
  }

  for (let index = 0; index < sourceSegments.length; index += 1) {
    const segment = sourceSegments[index];

    if (
      hasMinimumBoundaryDuration(segment.startTime, safePlayheadTime, minimumDuration) &&
      hasMinimumBoundaryDuration(safePlayheadTime, segment.endTime, minimumDuration)
    ) {
      const newSegment = createDraftSegment(safePlayheadTime, segment.endTime);
      const nextSegments = [...sourceSegments];
      nextSegments[index] = {
        ...segment,
        endTime: safePlayheadTime
      };
      nextSegments.splice(index + 1, 0, newSegment);
      return buildDraftResult(nextSegments, newSegment);
    }

    if (safePlayheadTime <= segment.startTime) {
      const previousSegment = sourceSegments[index - 1];
      const startTime = previousSegment ? previousSegment.endTime : 0;
      if (!hasMinimumBoundaryDuration(startTime, safePlayheadTime, minimumDuration)) {
        return { segments: sourceSegments, addedSegment: null };
      }

      const newSegment = createDraftSegment(startTime, safePlayheadTime);
      const nextSegments = [...sourceSegments];
      nextSegments.splice(index, 0, newSegment);
      return buildDraftResult(nextSegments, newSegment);
    }
  }

  const lastSegment = sourceSegments[sourceSegments.length - 1];
  if (!hasMinimumBoundaryDuration(lastSegment.endTime, safePlayheadTime, minimumDuration)) {
    return { segments: sourceSegments, addedSegment: null };
  }

  const newSegment = createDraftSegment(lastSegment.endTime, safePlayheadTime);
  return buildDraftResult([...sourceSegments, newSegment], newSegment);
};

const getSegmentIndex = (segments: Segment[], segmentId: string): number => {
  return segments.findIndex(segment => segment.id === segmentId);
};

const GENERATED_COMBO_NAME_PATTERN = /^Combo\s+\d+\s*-\s*\d+$/i;
const GENERATED_LEGACY_COMBO_NAME_PATTERN = /^Segment\s+\d+\s*_\s*\d+$/i;

export const isGeneratedComboName = (name: string): boolean => {
  return GENERATED_COMBO_NAME_PATTERN.test(name.trim());
};

const isGeneratedLegacyComboName = (name: string): boolean => {
  return GENERATED_LEGACY_COMBO_NAME_PATTERN.test(name.trim());
};

const shouldAutoUpdateComboName = (combo: Segment): boolean => {
  if (combo.comboNameEdited) return false;
  return isGeneratedComboName(combo.name) || isGeneratedLegacyComboName(combo.name);
};

const getSourceSegmentNumber = (sourceSegments: Segment[], segmentId: string | null | undefined): number => {
  if (!segmentId) return 0;
  const index = getSegmentIndex(sourceSegments, segmentId);
  return index >= 0 ? index + 1 : 0;
};

export const getSourceSegmentTimelineId = (
  segments: Segment[],
  segmentId: string | null | undefined
): string => {
  const segmentNumber = getSourceSegmentNumber(getSourceSegments(segments), segmentId);
  return segmentNumber > 0 ? String(segmentNumber) : '';
};

export const getSourceSegmentDisplayTitle = (
  segment: Segment,
  timelineId = ''
): string => {
  if (!segment.segmentNameEdited && timelineId && isGeneratedSegmentName(segment.name)) {
    return `Segment ${timelineId}`;
  }
  return segment.name;
};

export const getSourceSegmentDisplayLabel = (
  segments: Segment[],
  segment: Segment
): string => {
  const timelineId = getSourceSegmentTimelineId(segments, segment.id);
  const title = getSourceSegmentDisplayTitle(segment, timelineId);
  if (!timelineId || (!segment.segmentNameEdited && isGeneratedSegmentName(segment.name))) {
    return title;
  }
  return `${timelineId} ${title}`;
};

export const orderSourceSegmentIds = (
  segments: Segment[],
  sourceSegmentIds: string[]
): string[] => {
  const sourceSegments = getSourceSegments(segments);
  const seen = new Set<string>();

  return sourceSegmentIds
    .filter(segmentId => {
      if (seen.has(segmentId)) return false;
      seen.add(segmentId);
      return getSegmentIndex(sourceSegments, segmentId) >= 0;
    })
    .sort((a, b) => getSegmentIndex(sourceSegments, a) - getSegmentIndex(sourceSegments, b));
};

const resolveComboOriginSegmentId = (
  segments: Segment[],
  orderedSourceIds: string[],
  originSegmentId?: string
): string | null => {
  const sourceSegments = getSourceSegments(segments);
  if (originSegmentId && getSegmentIndex(sourceSegments, originSegmentId) >= 0) {
    return originSegmentId;
  }
  return orderedSourceIds[0] ?? null;
};

const getDefaultComboEdgeSegmentId = (
  segments: Segment[],
  orderedSourceIds: string[],
  originSegmentId?: string | null
): string | null => {
  if (orderedSourceIds.length === 0) return null;
  if (orderedSourceIds.length === 1) return orderedSourceIds[0];

  const sourceSegments = getSourceSegments(segments);
  const originIndex = originSegmentId ? getSegmentIndex(sourceSegments, originSegmentId) : -1;
  const lastIndex = getSegmentIndex(sourceSegments, orderedSourceIds[orderedSourceIds.length - 1]);

  if (originIndex >= lastIndex) return orderedSourceIds[0];
  return orderedSourceIds[orderedSourceIds.length - 1];
};

export const getComboName = (
  segments: Segment[],
  sourceSegmentIds: string[],
  fallbackName = 'Combo',
  _originSegmentId?: string,
  _edgeSegmentId?: string
): string => {
  const orderedIds = orderSourceSegmentIds(segments, sourceSegmentIds);
  const sourceSegments = getSourceSegments(segments);
  const firstNumber = getSourceSegmentNumber(sourceSegments, orderedIds[0]);
  const lastNumber = getSourceSegmentNumber(sourceSegments, orderedIds[orderedIds.length - 1]);

  if (firstNumber === 0 || lastNumber === 0) return fallbackName;
  return `Combo ${firstNumber}-${lastNumber}`;
};

export const getComboRange = (
  segments: Segment[],
  combo: Segment | null | undefined
): ComboRange | null => {
  if (!combo) return null;

  const sourceSegmentIds = orderSourceSegmentIds(segments, combo.sourceSegmentIds ?? []);
  const sourceSegments = getSourceSegments(segments);
  const comboSourceSegments = sourceSegmentIds
    .map(segmentId => sourceSegments.find(segment => segment.id === segmentId))
    .filter((segment): segment is Segment => Boolean(segment));

  if (comboSourceSegments.length === 0) {
    if (!isCombo(combo)) return null;

    return {
      sourceSegmentIds: [],
      sourceSegments: [],
      startTime: combo.startTime,
      endTime: combo.endTime,
      startIndex: -1,
      endIndex: -1,
      duration: Math.max(0, combo.endTime - combo.startTime),
      color: COMBO_SEGMENT_COLOR,
      originSegmentId: combo.originSegmentId
    };
  }

  const startSegment = comboSourceSegments[0];
  const endSegment = comboSourceSegments[comboSourceSegments.length - 1];
  const startIndex = getSegmentIndex(sourceSegments, startSegment.id);
  const endIndex = getSegmentIndex(sourceSegments, endSegment.id);
  const requestedOriginSegment = combo.originSegmentId
    ? sourceSegments.find(segment => segment.id === combo.originSegmentId)
    : null;
  const originSegment = requestedOriginSegment
    ? comboSourceSegments.find(segment => segment.id === requestedOriginSegment.id)
    : null;
  const colorSource = originSegment ?? startSegment;

  return {
    sourceSegmentIds,
    sourceSegments: comboSourceSegments,
    startTime: startSegment.startTime,
    endTime: endSegment.endTime,
    startIndex,
    endIndex,
    duration: Math.max(0, endSegment.endTime - startSegment.startTime),
    color: COMBO_SEGMENT_COLOR,
    originSegmentId: requestedOriginSegment?.id ?? colorSource.id
  };
};

export const getComboTimelineId = (
  range: Pick<ComboRange, 'startIndex' | 'endIndex'> | null | undefined
): string => {
  if (!range || range.startIndex < 0 || range.endIndex < 0) return '';
  const startNumber = range.startIndex + 1;
  const endNumber = range.endIndex + 1;
  return startNumber === endNumber ? String(startNumber) : `${startNumber}-${endNumber}`;
};

export const getComboDisplayTitle = (
  combo: Segment,
  timelineId = ''
): string => {
  if (!combo.comboNameEdited && timelineId && isGeneratedComboName(combo.name)) {
    return `Combo ${timelineId}`;
  }
  return combo.name;
};

export const getComboDisplayLabel = (
  combo: Segment,
  timelineId = ''
): string => {
  const title = getComboDisplayTitle(combo, timelineId);
  if (!timelineId || (!combo.comboNameEdited && isGeneratedComboName(combo.name))) {
    return title;
  }
  return `Combo ${timelineId} ${title}`;
};

const compareComboRangesBySmallest = (
  a: { segment: Segment; range: ComboRange },
  b: { segment: Segment; range: ComboRange }
): number => (
  a.range.duration - b.range.duration ||
  a.range.sourceSegmentIds.length - b.range.sourceSegmentIds.length ||
  a.range.startIndex - b.range.startIndex ||
  a.range.endIndex - b.range.endIndex ||
  a.segment.name.localeCompare(b.segment.name)
);

export const getRelatedComboIdForSourceSegment = (
  segments: Segment[],
  sourceSegmentId: string
): string | null => {
  const sourceSegments = getSourceSegments(segments);
  if (!sourceSegments.some(segment => segment.id === sourceSegmentId)) {
    return null;
  }

  const relatedCombos = segments.reduce<Array<{ segment: Segment; range: ComboRange }>>((matches, combo) => {
    if (!isCombo(combo)) return matches;

    const range = getComboRange(segments, combo);
    if (
      !range ||
      range.sourceSegmentIds.length < MIN_COMBO_SOURCE_SEGMENTS ||
      !range.sourceSegmentIds.includes(sourceSegmentId)
    ) {
      return matches;
    }

    matches.push({ segment: combo, range });
    return matches;
  }, []);

  return relatedCombos.sort(compareComboRangesBySmallest)[0]?.segment.id ?? null;
};

export const resolveActiveComboId = (
  segments: Segment[],
  activeSegmentId: string | null,
  fallbackComboId?: string | null
): string | null => {
  const activeSegment = activeSegmentId
    ? segments.find(segment => segment.id === activeSegmentId)
    : null;

  if (
    activeSegment &&
    isCombo(activeSegment) &&
    (activeSegment.sourceSegmentIds?.length ?? MIN_COMBO_SOURCE_SEGMENTS) >= MIN_COMBO_SOURCE_SEGMENTS
  ) {
    return activeSegment.id;
  }

  if (activeSegment && !isCombo(activeSegment)) {
    return getRelatedComboIdForSourceSegment(segments, activeSegment.id);
  }

  if (fallbackComboId && segments.some(segment =>
    segment.id === fallbackComboId &&
    isCombo(segment) &&
    (segment.sourceSegmentIds?.length ?? MIN_COMBO_SOURCE_SEGMENTS) >= MIN_COMBO_SOURCE_SEGMENTS
  )) {
    return fallbackComboId;
  }

  return getFirstComboId(segments);
};

const getLegacyComboName = (
  legacyName: string,
  segments: Segment[],
  sourceSegmentIds: string[]
): string => {
  const generatedLegacyName = isGeneratedLegacyComboName(legacyName);
  return generatedLegacyName ? getComboName(segments, sourceSegmentIds, 'Combo') : legacyName;
};

const getAutoComboName = (
  segments: Segment[],
  combo: Segment,
  sourceSegmentIds: string[],
  edgeSegmentId?: string
): string => {
  if (!shouldAutoUpdateComboName(combo)) return combo.name;
  return getComboName(
    segments,
    sourceSegmentIds,
    combo.name,
    combo.originSegmentId,
    edgeSegmentId
  );
};

export const inferLegacyComboSourceIds = (
  segments: Segment[],
  combo: Segment
): string[] => {
  const epsilon = 0.01;
  return getSourceSegments(segments)
    .filter(segment =>
      segment.endTime > combo.startTime + epsilon &&
      segment.startTime < combo.endTime - epsilon
    )
    .map(segment => segment.id);
};

export const deriveComboSegment = (
  segments: Segment[],
  combo: Segment,
  sourceSegmentIds: string[],
  name?: string
): Segment => {
  const orderedSourceIds = orderSourceSegmentIds(segments, sourceSegmentIds);
  const rangeSeed: Segment = {
    ...combo,
    isCombo: true,
    sourceSegmentIds: orderedSourceIds
  };
  const range = getComboRange(segments, rangeSeed);

  if (!range || orderedSourceIds.length === 0) {
    return {
      ...combo,
      ...(name ? { name } : {}),
      isCombo: true,
      sourceSegmentIds: orderedSourceIds
    };
  }

  return {
    ...combo,
    ...(name ? { name } : {}),
    startTime: range.startTime,
    endTime: range.endTime,
    color: range.color,
    isCombo: true,
    sourceSegmentIds: orderedSourceIds,
    originSegmentId: range.originSegmentId
  };
};

export const createComboFromSourceIds = (
  segments: Segment[],
  sourceSegmentIds: string[],
  transitionDuration: number,
  id: string = `combo-${Date.now()}`,
  originSegmentId?: string,
  edgeSegmentId?: string
): Segment | null => {
  const orderedSourceIds = orderSourceSegmentIds(segments, sourceSegmentIds);
  if (orderedSourceIds.length < MIN_COMBO_SOURCE_SEGMENTS) return null;
  const resolvedOriginSegmentId = resolveComboOriginSegmentId(segments, orderedSourceIds, originSegmentId);
  const resolvedEdgeSegmentId = edgeSegmentId && orderedSourceIds.includes(edgeSegmentId)
    ? edgeSegmentId
    : getDefaultComboEdgeSegmentId(segments, orderedSourceIds, resolvedOriginSegmentId);

  const baseCombo: Segment = {
    id,
    name: getComboName(
      segments,
      orderedSourceIds,
      'Combo',
      resolvedOriginSegmentId ?? undefined,
      resolvedEdgeSegmentId ?? undefined
    ),
    startTime: 0,
    endTime: 0,
    color: COMBO_SEGMENT_COLOR,
    transitionDuration,
    isLearned: false,
    isDisabled: false,
    isCombo: true,
    sourceSegmentIds: orderedSourceIds,
    originSegmentId: resolvedOriginSegmentId ?? orderedSourceIds[0],
    comboNameEdited: false
  };

  return deriveComboSegment(segments, baseCombo, orderedSourceIds);
};

export const normalizeCombos = (segments: Segment[]): Segment[] => {
  if (segments.length === 0) return segments;

  let didChange = false;
  const normalized = segments.flatMap(segment => {
    if (!isCombo(segment)) return [segment];

    const explicitSourceIds = segment.sourceSegmentIds ?? [];
    const sourceSegmentIds = explicitSourceIds.length > 0
      ? orderSourceSegmentIds(segments, explicitSourceIds)
      : inferLegacyComboSourceIds(segments, segment);

    if (sourceSegmentIds.length < MIN_COMBO_SOURCE_SEGMENTS) {
      didChange = true;
      return [];
    }

    const nextName = explicitSourceIds.length > 0
      ? shouldAutoUpdateComboName(segment)
        ? getComboName(segments, sourceSegmentIds, segment.name)
        : segment.name
      : getLegacyComboName(segment.name, segments, sourceSegmentIds);
    const nextSegment = deriveComboSegment(segments, { ...segment, name: nextName }, sourceSegmentIds);

    didChange = didChange ||
      nextSegment.isCombo !== segment.isCombo ||
      nextSegment.name !== segment.name ||
      nextSegment.startTime !== segment.startTime ||
      nextSegment.endTime !== segment.endTime ||
      nextSegment.color !== segment.color ||
      nextSegment.originSegmentId !== segment.originSegmentId ||
      JSON.stringify(nextSegment.sourceSegmentIds ?? []) !== JSON.stringify(segment.sourceSegmentIds ?? []);

    return [nextSegment];
  });

  return didChange ? normalized : segments;
};

const doSegmentsOverlap = (
  segment: Pick<Segment, 'startTime' | 'endTime'>,
  startTime: number,
  endTime: number
): boolean => {
  const epsilon = 0.001;
  return segment.endTime > startTime + epsilon && segment.startTime < endTime - epsilon;
};

export const applyBoundaryDraftSegments = (
  segments: Segment[],
  boundaryDraftSegments: Segment[]
): Segment[] => {
  const draftSourceSegments = renameGeneratedSourceSegmentsByTimeline(getSourceSegments(boundaryDraftSegments));
  if (draftSourceSegments.length === 0) {
    return normalizeCombos(segments);
  }

  const normalizedBaseSegments = normalizeCombos(segments);
  const baseSourceById = new Map(
    getSourceSegments(normalizedBaseSegments).map(segment => [segment.id, segment] as const)
  );
  const repairedCombos = normalizedBaseSegments
    .filter(isCombo)
    .flatMap(combo => {
      const previousSourceIds = combo.sourceSegmentIds ?? [];
      const previousSourceIdSet = new Set(previousSourceIds);
      const previousSourceSegments = previousSourceIds
        .map(sourceId => baseSourceById.get(sourceId))
        .filter((segment): segment is Segment => Boolean(segment));
      const nextSourceIds = draftSourceSegments
        .filter(draftSegment => {
          if (previousSourceIdSet.has(draftSegment.id)) return true;
          return previousSourceSegments.some(previousSegment =>
            doSegmentsOverlap(draftSegment, previousSegment.startTime, previousSegment.endTime)
          );
        })
        .map(segment => segment.id);

      if (nextSourceIds.length < MIN_COMBO_SOURCE_SEGMENTS) return [];

      const sourceIdsChanged =
        JSON.stringify(nextSourceIds) !== JSON.stringify(previousSourceIds);
      const nextName = sourceIdsChanged && shouldAutoUpdateComboName(combo)
        ? getComboName(
            draftSourceSegments,
            nextSourceIds,
            combo.name,
            combo.originSegmentId
          )
        : combo.name;

      return [
        deriveComboSegment(
          draftSourceSegments,
          { ...combo, name: nextName },
          nextSourceIds,
          nextName
        )
      ];
    });

  return normalizeCombos([...draftSourceSegments, ...repairedCombos]);
};

export const removeSegmentAndRepairCombos = (
  segments: Segment[],
  segmentId: string
): Segment[] => {
  const normalizedSegments = normalizeCombos(segments);
  const segmentToRemove = normalizedSegments.find(segment => segment.id === segmentId);
  if (!segmentToRemove) return normalizedSegments;

  if (isCombo(segmentToRemove)) {
    return normalizedSegments.filter(segment => segment.id !== segmentId);
  }

  const withoutSegment = renameGeneratedSourceSegmentsByTimeline(
    normalizedSegments.filter(segment => segment.id !== segmentId)
  );
  const repairedSegments = withoutSegment.flatMap(segment => {
    if (!isCombo(segment)) return [segment];

    const nextSourceIds = (segment.sourceSegmentIds ?? []).filter(sourceId => sourceId !== segmentId);
    if (nextSourceIds.length < MIN_COMBO_SOURCE_SEGMENTS) return [];
    return [deriveComboSegment(withoutSegment, segment, nextSourceIds)];
  });

  return normalizeCombos(repairedSegments);
};

export const removeSegmentsAndRepairCombos = (
  segments: Segment[],
  segmentIds: Iterable<string>
): Segment[] => {
  const idsToRemove = new Set(segmentIds);
  if (idsToRemove.size === 0) return segments;

  let nextSegments = normalizeCombos(segments);
  idsToRemove.forEach(segmentId => {
    nextSegments = removeSegmentAndRepairCombos(nextSegments, segmentId);
  });
  return nextSegments;
};

export const duplicateComboSegment = (
  segments: Segment[],
  comboId: string,
  id: string = `combo-${Date.now()}`
): Segment | null => {
  const normalizedSegments = normalizeCombos(segments);
  const combo = normalizedSegments.find(segment => segment.id === comboId && isCombo(segment));
  if (!combo || (combo.sourceSegmentIds ?? []).length < MIN_COMBO_SOURCE_SEGMENTS) return null;

  const duplicate: Segment = {
    ...combo,
    id,
    name: `${combo.name} copy`,
    isLearned: false,
    isCombo: true
  };

  return deriveComboSegment(normalizedSegments, duplicate, combo.sourceSegmentIds ?? []);
};

export const getComboSourceIdsForEdgeTime = (
  segments: Segment[],
  combo: Segment,
  edge: ComboEdge,
  time: number
): string[] => {
  const sourceSegments = getSourceSegments(segments);
  const range = getComboRange(segments, combo);
  if (!range || range.startIndex < 0 || range.endIndex < 0) {
    return combo.sourceSegmentIds ?? [];
  }

  let nearestIndex = edge === 'left' ? range.startIndex : range.endIndex;
  let nearestDistance = Number.POSITIVE_INFINITY;

  sourceSegments.forEach((segment, index) => {
    const boundary = edge === 'left' ? segment.startTime : segment.endTime;
    const distance = Math.abs(boundary - time);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestIndex = index;
    }
  });

  let nextStartIndex = edge === 'left'
    ? Math.min(nearestIndex, range.endIndex)
    : range.startIndex;
  let nextEndIndex = edge === 'right'
    ? Math.max(nearestIndex, range.startIndex)
    : range.endIndex;
  const originIndex = combo.originSegmentId
    ? getSegmentIndex(sourceSegments, combo.originSegmentId)
    : -1;

  if (originIndex >= 0) {
    nextStartIndex = Math.min(nextStartIndex, originIndex);
    nextEndIndex = Math.max(nextEndIndex, originIndex);
  }

  return sourceSegments
    .slice(nextStartIndex, nextEndIndex + 1)
    .map(segment => segment.id);
};

export const resizeComboToEdgeTime = (
  segments: Segment[],
  combo: Segment,
  edge: ComboEdge,
  time: number
): Segment | null => {
  const nextSourceIds = getComboSourceIdsForEdgeTime(segments, combo, edge, time);
  if (nextSourceIds.length < MIN_COMBO_SOURCE_SEGMENTS) return null;

  const edgeSegmentId = edge === 'left'
    ? nextSourceIds[0]
    : nextSourceIds[nextSourceIds.length - 1];

  return deriveComboSegment(
    segments,
    combo,
    nextSourceIds,
    getAutoComboName(segments, combo, nextSourceIds, edgeSegmentId)
  );
};

export const nudgeComboEdge = (
  segments: Segment[],
  combo: Segment,
  edge: ComboEdge,
  direction: -1 | 1
): Segment | null => {
  const sourceSegments = getSourceSegments(segments);
  const range = getComboRange(segments, combo);
  if (!range || range.startIndex < 0 || range.endIndex < 0) return combo;

  let nextStartIndex = edge === 'left'
    ? Math.min(range.endIndex, Math.max(0, range.startIndex + direction))
    : range.startIndex;
  let nextEndIndex = edge === 'right'
    ? Math.max(range.startIndex, Math.min(sourceSegments.length - 1, range.endIndex + direction))
    : range.endIndex;
  const originIndex = combo.originSegmentId
    ? getSegmentIndex(sourceSegments, combo.originSegmentId)
    : -1;

  if (originIndex >= 0) {
    nextStartIndex = Math.min(nextStartIndex, originIndex);
    nextEndIndex = Math.max(nextEndIndex, originIndex);
  }

  const nextSourceIds = sourceSegments
    .slice(nextStartIndex, nextEndIndex + 1)
    .map(segment => segment.id);
  if (nextSourceIds.length < MIN_COMBO_SOURCE_SEGMENTS) return null;

  const edgeSegmentId = edge === 'left'
    ? nextSourceIds[0]
    : nextSourceIds[nextSourceIds.length - 1];

  return deriveComboSegment(
    segments,
    combo,
    nextSourceIds,
    getAutoComboName(segments, combo, nextSourceIds, edgeSegmentId)
  );
};

/**
 * Merge learned segments into a new segment
 */
export const mergeLearnedSegments = (
  segments: Segment[],
  transitionDuration: number
): Segment | null => {
  const learned = segments.filter(segment => !isCombo(segment) && segment.isLearned);
  
  if (learned.length < 2) {
    return null;
  }
  
  learned.sort((a, b) => a.startTime - b.startTime);
  const combo = createComboFromSourceIds(
    segments,
    learned.map(segment => segment.id),
    transitionDuration,
    `combo-${Date.now()}`,
    learned[0].id,
    learned[learned.length - 1].id
  );

  return combo;
};

/**
 * Handle smooth transitions during looping
 */
export const handleSmoothTransition = (
  video: HTMLVideoElement,
  segment: Segment,
  currentPlaybackRate: number
): void => {
  const timeToEnd = segment.endTime - video.currentTime;
  
  if (timeToEnd < segment.transitionDuration && timeToEnd > 0) {
    const slowFactor = timeToEnd / segment.transitionDuration;
    video.playbackRate = currentPlaybackRate * Math.max(0.3, slowFactor);
  }
};

/**
 * Calculate timeline position for a given time
 */
export const getTimelinePosition = (time: number, duration: number): string => {
  return `${(time / duration) * 100}%`;
};

/**
 * Calculate time from timeline position
 */
export const getTimeFromPosition = (position: number, duration: number): number => {
  return position * duration;
};

/**
 * Check if a segment is wide enough to show labels
 */
export const shouldShowSegmentLabel = (segment: Segment, duration: number): boolean => {
  return ((segment.endTime - segment.startTime) / duration) > 0.10;
};

export type TimelineLabelMode = 'none' | 'number' | 'name';

export type TimelineLabelKind = 'source' | 'combo';

export interface TimelineLabelModeOptions {
  zoomLevel?: number;
  timelineWidthPx?: number | null;
  numberLabel?: string;
  nameLabel?: string;
  kind?: TimelineLabelKind;
  isActive?: boolean;
  isHovered?: boolean;
  isSelected?: boolean;
  showAllCombos?: boolean;
}

const TIMELINE_LABEL_INLINE_GAP_PX = 4;
const TIMELINE_LABEL_HORIZONTAL_BUFFER_PX = 18;
const TIMELINE_LABEL_MIN_NUMBER_WIDTH_PX = 20;

const estimateTimelineLabelWidth = (label: string): number => {
  const trimmedLabel = label.trim();
  if (!trimmedLabel) return 0;

  const width = trimmedLabel.split('').reduce((totalWidth, character) => {
    if (character === ' ') return totalWidth + 3.2;
    if (/[-:]/.test(character)) return totalWidth + 3.8;
    if (/[ilI1]/.test(character)) return totalWidth + 3.8;
    if (/[0-9]/.test(character)) return totalWidth + 5.8;
    if (/[A-Z]/.test(character)) return totalWidth + 6.6;
    return totalWidth + 5.7;
  }, 0);

  return Math.ceil(width);
};

const getLabelModeFromPixelWidth = (
  widthPx: number,
  numberLabel: string,
  nameLabel: string
): TimelineLabelMode | null => {
  if (!Number.isFinite(widthPx) || widthPx <= 0) return null;

  const numberWidth = Math.max(
    TIMELINE_LABEL_MIN_NUMBER_WIDTH_PX,
    estimateTimelineLabelWidth(numberLabel) + TIMELINE_LABEL_HORIZONTAL_BUFFER_PX
  );
  const nameWidth = Math.max(
    numberWidth + TIMELINE_LABEL_INLINE_GAP_PX + 14,
    estimateTimelineLabelWidth(nameLabel) + TIMELINE_LABEL_HORIZONTAL_BUFFER_PX
  );

  if (widthPx >= nameWidth) return 'name';
  if (widthPx >= numberWidth) return 'number';
  return 'none';
};

/**
 * Choose the most detailed timeline label that can fit the current visual width.
 * The rendered width scales with zoom, while the stored segment times do not.
 */
export const getTimelineSegmentLabelMode = (
  segment: Pick<Segment, 'startTime' | 'endTime'>,
  duration: number,
  options: TimelineLabelModeOptions = {}
): TimelineLabelMode => {
  if (duration <= 0 || !Number.isFinite(duration)) return 'none';

  const segmentDuration = Math.max(segment.endTime - segment.startTime, 0);
  if (segmentDuration <= 0) return 'none';

  const zoomLevel = Math.max(1, Number.isFinite(options.zoomLevel) ? options.zoomLevel ?? 1 : 1);
  const durationRatio = segmentDuration / duration;
  const measuredTimelineWidth = Number.isFinite(options.timelineWidthPx)
    ? options.timelineWidthPx ?? null
    : null;
  const measuredWidthMode = measuredTimelineWidth && measuredTimelineWidth > 0
    ? getLabelModeFromPixelWidth(
        durationRatio * measuredTimelineWidth,
        options.numberLabel ?? '1',
        options.nameLabel ?? options.numberLabel ?? '1'
      )
    : null;
  const visualRatio = durationRatio * zoomLevel;
  const kind = options.kind ?? 'source';
  const isEmphasized = Boolean(options.isActive || options.isHovered || options.isSelected);

  if (kind === 'combo') {
    if (measuredWidthMode) {
      if (!isEmphasized && options.showAllCombos) return 'none';
      return measuredWidthMode;
    }

    if (isEmphasized) {
      if (visualRatio >= 0.16) return 'name';
      if (visualRatio >= 0.045) return 'number';
      return 'none';
    }

    if (options.showAllCombos) {
      return visualRatio >= 0.46 ? 'name' : 'none';
    }

    if (visualRatio >= 0.16) return 'name';
    if (visualRatio >= 0.06) return 'number';
    return 'none';
  }

  if (measuredWidthMode) return measuredWidthMode;

  if (isEmphasized) {
    if (visualRatio >= 0.16) return 'name';
    if (visualRatio >= 0.018) return 'number';
    return 'none';
  }

  if (visualRatio >= 0.16) return 'name';
  if (visualRatio >= 0.055) return 'number';
  return 'none';
};

/**
 * Save and load session data to/from Firebase
 */
export const saveSessionToFirebase = async (
  videoFile: File,
  segments: Segment[],
  settings: VideoSettings,
  filename: string
) => {
  return await saveToFirebase(
    videoFile,
    normalizeCombos(segments),
    normalizeVideoSettingsViewCrop(settings) as VideoSettings,
    filename
  );
};

export const loadSessionFromFirebase = async (sessionId: string): Promise<SessionData> => {
  const session = await loadFromFirebase(sessionId) as SessionData;
  return {
    ...session,
    segments: normalizeCombos(session.segments || []),
    settings: normalizeVideoSettingsViewCrop(session.settings || {}) as VideoSettings
  };
};

export const userDocRef = (uid: string) => doc(db, `${USERS_COLL}/${uid}`);

export const userVideosCollRef = (uid: string) =>
  collection(db, `${USERS_COLL}/${uid}/${USER_VIDEOS_SUBCOLL}`);

export const userVideoDocRef = (uid: string, videoId: string) =>
  doc(db, `${USERS_COLL}/${uid}/${USER_VIDEOS_SUBCOLL}/${videoId}`);

export const userProjectsCollRef = (uid: string) =>
  collection(db, `${USERS_COLL}/${uid}/${USER_PROJECTS_SUBCOLL}`);

export const userProjectDocRef = (uid: string, projectId: string) =>
  doc(db, `${USERS_COLL}/${uid}/${USER_PROJECTS_SUBCOLL}/${projectId}`);

export const userPlaylistsCollRef = (uid: string) =>
  collection(db, `${USERS_COLL}/${uid}/${USER_PLAYLISTS_SUBCOLL}`);

export const userPlaylistDocRef = (uid: string, playlistId: string) =>
  doc(db, `${USERS_COLL}/${uid}/${USER_PLAYLISTS_SUBCOLL}/${playlistId}`);

export const userPlaylistItemsCollRef = (uid: string, playlistId: string) =>
  collection(db, `${USERS_COLL}/${uid}/${USER_PLAYLISTS_SUBCOLL}/${playlistId}/items`);

export const userPlaylistItemDocRef = (
  uid: string,
  playlistId: string,
  membershipId: string
) =>
  doc(db, `${USERS_COLL}/${uid}/${USER_PLAYLISTS_SUBCOLL}/${playlistId}/items/${membershipId}`);

// `extension` defaults to mp4 so callers that only know a video id (legacy
// delete paths) keep resolving the historical object name.
export const userVideoStoragePath = (
  uid: string,
  videoId: string,
  extension: string = DEFAULT_VIDEO_EXTENSION
) =>
  `${STORAGE_USER_ROOT}/${uid}/${STORAGE_USER_VIDEOS}/${videoId}.${extension}`;

export const userVideoStorageRef = (
  uid: string,
  videoId: string,
  extension: string = DEFAULT_VIDEO_EXTENSION
) =>
  ref(storage, userVideoStoragePath(uid, videoId, extension));


// utils.ts
export const DEFAULT_SEGMENT_OVERLAP_SECONDS = 1;

export const clamp01 = (x: number) => Math.max(0, Math.min(1, x));

const isFiniteNumber = (value: unknown): value is number => (
  typeof value === 'number' && Number.isFinite(value)
);

export const getSegmentDuration = (segment: Pick<Segment, 'startTime' | 'endTime'>): number => (
  Math.max(0, segment.endTime - segment.startTime)
);

export const getLargestSegmentDuration = (segments: Array<Pick<Segment, 'startTime' | 'endTime'>>): number => (
  segments.reduce((largestDuration, segment) => Math.max(largestDuration, getSegmentDuration(segment)), 0)
);

export const clampSegmentOverlapSeconds = (
  overlapSeconds: number,
  maxOverlapSeconds: number
): number => {
  const safeOverlapSeconds = Number.isFinite(overlapSeconds) ? overlapSeconds : DEFAULT_SEGMENT_OVERLAP_SECONDS;
  const safeMaxOverlapSeconds = Number.isFinite(maxOverlapSeconds) ? Math.max(0, maxOverlapSeconds) : 0;

  if (safeMaxOverlapSeconds <= 0) return Math.max(0, safeOverlapSeconds);

  return Math.min(Math.max(0, safeOverlapSeconds), safeMaxOverlapSeconds);
};

/** Convert overlap fraction (0..1) to preroll seconds based on half segment duration. */
export const overlapSecondsFromFraction = (
  startTime: number,
  endTime: number,
  overlapFraction: number
) => {
  const dur = Math.max(0, endTime - startTime);
  // Optional: cap at 95% so we never "eat" an entire segment
  const f = Math.min(0.95, clamp01(overlapFraction));
  return (dur * 0.5) * f;
};

export const resolveSegmentOverlapSeconds = (
  settings: Pick<VideoSettings, 'segmentOverlapSeconds'> | (Partial<VideoSettings> & Record<string, unknown>) | null | undefined,
  segments: Array<Pick<Segment, 'startTime' | 'endTime'>>,
  fallbackSeconds = DEFAULT_SEGMENT_OVERLAP_SECONDS
): number => {
  const largestSegmentDuration = getLargestSegmentDuration(segments);
  const settingsRecord = settings as Record<string, unknown> | null | undefined;
  const configuredOverlapSeconds = settingsRecord?.segmentOverlapSeconds;

  if (isFiniteNumber(configuredOverlapSeconds) && configuredOverlapSeconds >= 0) {
    return clampSegmentOverlapSeconds(configuredOverlapSeconds, largestSegmentDuration);
  }

  const configuredLegacyOverlap = settingsRecord?.segmentOverlap;
  const legacyOverlap = isFiniteNumber(configuredLegacyOverlap)
    ? configuredLegacyOverlap
    : null;

  if (legacyOverlap !== null && legacyOverlap >= 0) {
    const legacyOverlapSeconds = legacyOverlap <= 1 && largestSegmentDuration > 0
      ? overlapSecondsFromFraction(0, largestSegmentDuration, legacyOverlap)
      : legacyOverlap;

    return clampSegmentOverlapSeconds(legacyOverlapSeconds, largestSegmentDuration);
  }

  return clampSegmentOverlapSeconds(fallbackSeconds, largestSegmentDuration);
};
